import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVenue } from '@core/contexts/VenueContext';
import { useProfile } from '@core/contexts/ProfileContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { Button } from '@core/components/Button';
import { ApiError } from '@core/api/client';
import { grantConsent } from '@core/api/consent';
import type { PhoneLoginVenueOption } from '../api/auth';
import { clearReferralCode, pendingReferralCode } from '../utils/entryParams';
import { isValidLoginPhone, normalizeLoginPhone } from '../utils/phoneNumber';

type Step = 'phone' | 'code' | 'venue' | 'name';

// Только цифры телефона (для мягкой валидации на фронте; строгую E.164 проверяет бэк).
function phoneDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}

export function LoginPage() {
  const navigate = useNavigate();
  const { venue, venueSlug } = useVenue();
  const online = useOnlineStatus();
  const { requestCode, confirmCode, selectVenue, register } = useAuth();
  // Последняя версия refresh профиля: регистрация — async, и её замыкание видит refresh рендера «до входа».
  const { refresh: refreshProfile } = useProfile();
  const refreshProfileRef = useRef(refreshProfile);
  useEffect(() => {
    refreshProfileRef.current = refreshProfile;
  }, [refreshProfile]);

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // select_venue / register
  const [identityToken, setIdentityToken] = useState('');
  const [venues, setVenues] = useState<PhoneLoginVenueOption[]>([]);
  const [firstName, setFirstName] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(true);

  const home = '/';

  // Таймер повторной отправки.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const phoneValid = useMemo(() => isValidLoginPhone(phone), [phone]);
  const codeValid = phoneDigits(code).length >= 4;

  async function sendCode() {
    if (busy || !online) return;
    setBusy(true);
    setNotFound(false);
    try {
      // Номер — в виде, который принимает сервер (+7XXXXXXXXXX): ввод «8 (999) …» раньше получал 422.
      const res = await requestCode(normalizeLoginPhone(phone));
      setStep('code');
      setResendIn(res.retryAfterSeconds || 60);
    } catch {
      // client.ts уже показал тост; остаёмся на шаге телефона
    } finally {
      setBusy(false);
    }
  }

  async function submitCode() {
    if (busy || !codeValid || !online) return;
    setBusy(true);
    setNotFound(false);
    try {
      const out = await confirmCode(normalizeLoginPhone(phone), phoneDigits(code));
      if (out.status === 'authenticated') {
        navigate(home, { replace: true });
        return;
      }
      if (out.status === 'register') {
        // Аккаунтов нет — единый флоу продолжается регистрацией (имя + согласия).
        setIdentityToken(out.identityToken);
        setStep('name');
        return;
      }
      setIdentityToken(out.identityToken);
      setVenues(out.venues);
      setStep('venue');
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setNotFound(true);
      }
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  async function submitRegistration() {
    if (busy || !firstName.trim() || !online) return;
    setBusy(true);
    try {
      // Пришёл по ссылке друга (?ref=) — передаём код: сервер свяжет приглашение, как в мини-аппе.
      const customer = await register(identityToken, firstName.trim(), pendingReferralCode(venueSlug));
      clearReferralCode(venueSlug);
      // 152-ФЗ: фиксируем согласия сразу после создания аккаунта (cookie-сессия уже стоит).
      // Галка политики была обязательной на шаге телефона; маркетинг — по чекбоксу этого шага.
      try {
        await grantConsent(customer.id, 'PRIVACY', '1.0');
        if (marketingOptIn) {
          await grantConsent(customer.id, 'MARKETING', '1.0');
        }
      } catch {
        // согласия ретраятся ConsentGate'ом (экран согласия ядра) — регистрацию не валим
      }
      // Профиль загрузился сразу после входа, ещё без согласия: перечитываем, иначе ConsentGate личных экранов
      // повторно спросил бы согласие, которое клиент только что дал.
      await refreshProfileRef.current();
      navigate(home, { replace: true });
    } catch {
      // тост уже показан client'ом (PHONE_AMBIGUOUS / истёкший токен и т.п.)
    } finally {
      setBusy(false);
    }
  }

  async function pickVenue(venueId: string) {
    if (busy) return;
    setBusy(true);
    try {
      await selectVenue(identityToken, venueId);
      navigate(home, { replace: true });
    } catch {
      // тост уже показан
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-page text-text-main flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold">{venue?.name || 'Lunit Loyalty'}</h1>
          <p className="text-sm text-text-muted">Вход по номеру телефона</p>
        </div>

        {step === 'phone' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void sendCode();
            }}
          >
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7 (___) ___-__-__"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-brand bg-card border border-border px-4 py-3 text-lg outline-none focus:border-primary"
            />
            <label className="flex items-start gap-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Соглашаюсь с{' '}
                <Link to="/legal/privacy-policy" className="text-primary underline">
                  политикой конфиденциальности
                </Link>{' '}
                и{' '}
                <Link to="/legal/terms" className="text-primary underline">
                  условиями
                </Link>
                .
              </span>
            </label>
            <Button type="submit" fullWidth disabled={!phoneValid || !consent || !online || busy}>
              {busy ? 'Отправляем…' : 'Получить код'}
            </Button>
            {!online && <p className="text-xs text-center text-red-500">Нет соединения с интернетом.</p>}
          </form>
        )}

        {step === 'code' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode();
            }}
          >
            <p className="text-sm text-text-muted text-center">
              Вам поступит звонок — отвечать не нужно. Введите последние 4 цифры номера, с которого позвонили.
            </p>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="4 цифры"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={4}
              className="w-full rounded-brand bg-card border border-border px-4 py-3 text-center text-2xl tracking-widest outline-none focus:border-primary"
            />
            <Button type="submit" fullWidth disabled={!codeValid || !online || busy}>
              {busy ? 'Проверяем…' : 'Войти'}
            </Button>
            <div className="flex items-center justify-between text-xs text-text-muted">
              <button
                type="button"
                className="underline"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setNotFound(false);
                }}
              >
                Изменить номер
              </button>
              <button
                type="button"
                className="underline disabled:opacity-50"
                disabled={resendIn > 0 || busy}
                onClick={() => void sendCode()}
              >
                {resendIn > 0 ? `Повтор через ${resendIn}с` : 'Позвонить снова'}
              </button>
            </div>
            {notFound && (
              <div className="rounded-brand bg-card border border-border p-4 text-sm text-center">
                Неверный код. Проверьте последние 4 цифры номера, с которого поступил звонок.
              </div>
            )}
          </form>
        )}

        {step === 'name' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void submitRegistration();
            }}
          >
            <p className="text-sm text-text-muted text-center">
              Вы у нас впервые — как вас зовут?
            </p>
            <input
              type="text"
              autoComplete="given-name"
              placeholder="Ваше имя"
              value={firstName}
              maxLength={50}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-brand bg-card border border-border px-4 py-3 text-lg outline-none focus:border-primary"
            />
            <label className="flex items-start gap-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-0.5"
              />
              <span>Получать акции и персональные предложения</span>
            </label>
            <Button type="submit" fullWidth disabled={!firstName.trim() || !online || busy}>
              {busy ? 'Создаём…' : 'Создать карту лояльности'}
            </Button>
          </form>
        )}

        {step === 'venue' && (
          <div className="space-y-3">
            <p className="text-sm text-text-muted text-center">Выберите заведение:</p>
            {venues.map((v) => (
              <button
                key={v.venueId}
                type="button"
                disabled={busy}
                onClick={() => void pickVenue(v.venueId)}
                className="w-full rounded-brand bg-card border border-border px-4 py-3 text-left hover:bg-card-hover disabled:opacity-50"
              >
                {v.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
